// src/config/navConfig.js
// Single source of truth for navigation menu definitions

export const SECTIONS = {
  WORK: 'work',
  COMPLETED: 'completed',
  REFERENCE: 'reference',
  SYSTEM: 'system'
};

// Section display order (immutable)
export const SECTION_ORDER = [
  SECTIONS.WORK,
  SECTIONS.COMPLETED,
  SECTIONS.REFERENCE,
  SECTIONS.SYSTEM
];

// Section labels
export const SECTION_LABELS = {
  [SECTIONS.WORK]: 'Work',
  [SECTIONS.COMPLETED]: 'Manage',
  [SECTIONS.REFERENCE]: 'Resources',
  [SECTIONS.SYSTEM]: 'System'
};

// Badge keys - only Work items can have badges
export const BADGE_KEYS = {
  leads: 'leads_actionable',
  requestimates: 'requestimates_actionable',
  jobs: 'jobs_actionable',
  scheduled: 'scheduled_actionable',
  inProgress: 'inprogress_actionable',
  pickupDeliver: 'pickupdeliver_actionable',
  pickups: 'pickups_actionable',
  referrals: 'referrals_actionable',
  reviews: 'reviews_actionable',
  myWork: 'mywork_actionable',
  tasks: 'tasks_actionable',
  purchasing: 'purchasing_actionable'
};

// Menu item definitions
// Icons use FA Sharp Regular: "fa-sharp fa-regular fa-{name}"
export const MENU_ITEMS = {
  // Work items
  leads: {
    id: 'leads',
    label: 'Leads',
    route: '/referrals',
    icon: 'fa-sharp fa-regular fa-bolt',
    section: SECTIONS.WORK,
    badgeKey: BADGE_KEYS.leads
  },
  referrals: {
    id: 'referrals',
    label: 'Referrals',
    route: '/referrals',
    icon: 'fa-sharp fa-regular fa-paper-plane',
    section: SECTIONS.WORK,
    badgeKey: BADGE_KEYS.referrals
  },
  requestimates: {
    id: 'requestimates',
    label: 'Requestimates',
    route: '/requestimates',
    icon: 'fa-sharp fa-regular fa-file-invoice-dollar',
    section: SECTIONS.WORK,
    badgeKey: BADGE_KEYS.requestimates
  },
  requests: {
    id: 'requests',
    label: 'Requests',
    route: '/requestimates',
    icon: 'fa-sharp fa-regular fa-file-circle-plus',
    section: SECTIONS.WORK,
    badgeKey: BADGE_KEYS.requestimates
  },
  jobs: {
    id: 'jobs',
    label: 'Jobs',
    route: '/jobs',
    icon: 'fa-sharp fa-regular fa-briefcase',
    section: SECTIONS.WORK,
    badgeKey: BADGE_KEYS.jobs
  },
  scheduled: {
    id: 'scheduled',
    label: 'Scheduled',
    route: '/jobs?filter=scheduled',
    icon: 'fa-sharp fa-regular fa-calendar-check',
    section: SECTIONS.WORK,
    badgeKey: BADGE_KEYS.scheduled
  },
  inProgress: {
    id: 'inProgress',
    label: 'In Progress',
    route: '/jobs?filter=inprogress',
    icon: 'fa-sharp fa-regular fa-gears',
    section: SECTIONS.WORK,
    badgeKey: BADGE_KEYS.inProgress
  },
  pickupDeliver: {
    id: 'pickupDeliver',
    label: 'Pickup/Deliver',
    route: '/pickups',
    icon: 'fa-sharp fa-regular fa-truck-fast',
    section: SECTIONS.WORK,
    badgeKey: BADGE_KEYS.pickupDeliver
  },
  myWork: {
    id: 'myWork',
    label: 'My Work',
    route: '/mywork',
    icon: 'fa-sharp fa-regular fa-list-check',
    section: SECTIONS.WORK,
    badgeKey: BADGE_KEYS.myWork
  },
  myTime: {
    id: 'myTime',
    label: 'My Time',
    route: '/mytime',
    icon: 'fa-sharp fa-regular fa-stopwatch',
    section: SECTIONS.WORK
  },
  tasks: {
    id: 'tasks',
    label: 'Reminders',
    route: '/tasks',
    icon: 'fa-sharp fa-regular fa-bell',
    section: SECTIONS.WORK,
    badgeKey: BADGE_KEYS.tasks
  },

  // Completed/Manage items
  today: {
    id: 'today',
    label: 'Today',
    route: '/today',
    icon: 'fa-sharp fa-regular fa-sun',
    section: SECTIONS.COMPLETED
  },
  dashboard: {
    id: 'dashboard',
    label: 'Dashboard',
    route: '/dashboard',
    icon: 'fa-sharp fa-regular fa-chart-mixed',
    section: SECTIONS.COMPLETED
  },
  calendar: {
    id: 'calendar',
    label: 'Calendar',
    route: '/calendar',
    icon: 'fa-sharp fa-regular fa-calendar-days',
    section: SECTIONS.COMPLETED,
    warningKey: 'calendarNotConnected'
  },
  history: {
    id: 'history',
    label: 'History',
    route: '/history',
    icon: 'fa-sharp fa-regular fa-clock-rotate-left',
    section: SECTIONS.COMPLETED
  },
  financials: {
    id: 'financials',
    label: 'Financials',
    route: '/financials',
    icon: 'fa-sharp fa-regular fa-dollar-sign',
    section: SECTIONS.COMPLETED
  },
  time: {
    id: 'time',
    label: 'Time',
    route: '/staff/time-reports',
    icon: 'fa-sharp fa-regular fa-stopwatch',
    section: SECTIONS.COMPLETED
  },
  pickups: {
    id: 'pickups',
    label: 'Pickups',
    route: '/pickups',
    icon: 'fa-sharp fa-regular fa-truck-ramp-box',
    section: SECTIONS.WORK,
    badgeKey: BADGE_KEYS.pickups
  },
  reviews: {
    id: 'reviews',
    label: 'Reviews',
    route: '/reviews',
    icon: 'fa-sharp fa-regular fa-star-half-stroke',
    section: SECTIONS.WORK,
    badgeKey: BADGE_KEYS.reviews
  },

  // Reference items
  customers: {
    id: 'customers',
    label: 'Customers',
    route: '/customers',
    icon: 'fa-sharp fa-regular fa-users',
    section: SECTIONS.REFERENCE
  },
  vehicles: {
    id: 'vehicles',
    label: 'Vehicles',
    route: '/vehicles',
    icon: 'fa-sharp fa-regular fa-car',
    section: SECTIONS.REFERENCE
  },
  services: {
    id: 'services',
    label: 'Services',
    route: '/services',
    icon: 'fa-sharp fa-regular fa-screwdriver-wrench',
    section: SECTIONS.REFERENCE
  },
  inventory: {
    id: 'inventory',
    label: 'Inventory',
    route: '/inventory',
    icon: 'fa-sharp fa-regular fa-cubes',
    section: SECTIONS.REFERENCE
  },
  purchasing: {
    id: 'purchasing',
    label: 'Purchasing',
    route: '/purchasing',
    icon: 'fa-sharp fa-regular fa-cart-shopping',
    section: SECTIONS.REFERENCE,
    badgeKey: BADGE_KEYS.purchasing
  },
  staff: {
    id: 'staff',
    label: 'Staff',
    route: '/staff',
    icon: 'fa-sharp fa-regular fa-id-badge',
    section: SECTIONS.REFERENCE
  },

  // System items
  automations: {
    id: 'automations',
    label: 'Automations',
    route: '/campaigns',
    icon: 'fa-sharp fa-regular fa-wand-magic-sparkles',
    section: SECTIONS.SYSTEM,
    hasSubmenu: true,
    submenu: [
      { id: 'iris', label: 'Iris – Customer Experience', route: '/campaigns?agent=iris' },
      { id: 'luke', label: 'Luke – Re-Engagement', route: '/campaigns?agent=luke' }
    ]
  },
  userManagement: {
    id: 'userManagement',
    label: 'User Management',
    route: '/users',
    icon: 'fa-sharp fa-regular fa-user-gear',
    section: SECTIONS.SYSTEM
  },
  profile: {
    id: 'profile',
    label: 'Profile',
    route: '/profile',
    icon: 'fa-sharp fa-regular fa-circle-user',
    section: SECTIONS.SYSTEM,
    warningKey: 'stripeNotConnected'
  },
  systemHelp: {
    id: 'systemHelp',
    label: 'System Help',
    icon: 'fa-sharp fa-regular fa-circle-question',
    section: SECTIONS.SYSTEM,
    externalUrl: true,
    urls: {
      installer: 'https://www.jobvious.com/specialist-support-page',
      dealer: 'https://www.jobvious.com/dealer-support-page'
    }
  }
};
